<template>
    <div>
        <header class="fixed top-0 z-10 w-full bg-white" :class="{ 'shadow': !atTop }">
            <div class="container px-4 mx-auto">
                <div class="lg:flex-row lg:justify-between min-h-60px flex flex-col items-center justify-center">
                    <div class="lg:w-auto min-h-60px flex flex-row items-center justify-between w-full">
                        <a class="focus:no-underline hover:no-underline" href="javascript:;" onclick="document.documentElement.scrollIntoView({ behavior: 'smooth' });">
                            <logo-inverse></logo-inverse>
                        </a>
                        <button class="lg:hidden focus:outline-none focus:shadow-outline rounded-lg" @click="menuIsOpen = !menuIsOpen">
                            <svg class="w-6 h-6" stroke="currentColor" fill="none" viewBox="0 0 24 24">
                                <path :class="{ 'hidden': menuIsOpen, 'inline-flex': !menuIsOpen }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
                                <path :class="{ 'hidden': !menuIsOpen, 'inline-flex': menuIsOpen }" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                            </svg>
                        </button>
                    </div>
                    <nav :class="{ 'flex': menuIsOpen, 'hidden': !menuIsOpen }" class="lg:flex lg:flex-row lg:items-center lg:justify-end lg:py-0 lg:w-auto flex-col flex-grow w-full pb-6">
                        <a class="focus:no-underline hover:no-underline hover:text-blue-500 lg:mr-5 lg:mt-0 text-gray-body px-1 mt-2 font-medium"
                            id="counters-section-link"
                            href="javascript:;"
                            onclick="document.querySelector('#counters-section').scrollIntoView({ behavior: 'smooth' });">
                            {{ $t('marketing_pages.counters') }}
                        </a>
                        <a class="focus:no-underline hover:no-underline hover:text-blue-500 lg:mr-5 lg:mt-0 text-gray-body px-1 mt-2 font-medium"
                            id="features-section-link"
                            href="javascript:;"
                            onclick="document.querySelector('#features-section').scrollIntoView({ behavior: 'smooth' });">
                            {{ $t('marketing_pages.features') }}
                        </a>
                        <a class="focus:no-underline hover:no-underline hover:text-blue-500 lg:mr-5 lg:mt-0 text-gray-body px-1 mt-2 font-medium"
                            id="testimonials-section-link"
                            href="javascript:;"
                            onclick="document.querySelector('#testimonials-section').scrollIntoView({ behavior: 'smooth' });">
                            {{ $t('marketing_pages.testimonials') }}
                        </a>
                        <a class="focus:no-underline hover:no-underline hover:text-blue-500 lg:mr-5 lg:mt-0 text-gray-body px-1 mt-2 font-medium"
                            id="prices-section-link"
                            href="javascript:;"
                            onclick="document.querySelector('#prices-section').scrollIntoView({ behavior: 'smooth' });">
                            {{ $t('marketing_pages.prices') }}
                        </a>
                        <a class="focus:no-underline hover:no-underline hover:text-blue-500 lg:mr-5 lg:mt-0 text-gray-body px-1 mt-2 font-medium"
                            id="faq-section-link"
                            href="javascript:;"
                            onclick="document.querySelector('#faq-section').scrollIntoView({ behavior: 'smooth' });">
                            {{ $t('marketing_pages.faq') }}
                        </a>
                        <a class="focus:no-underline hover:bg-blue-500 hover:no-underline hover:text-white lg:mr-5 lg:mt-0 py-5px px-3 mt-2 font-medium text-blue-500 bg-white border border-blue-500 rounded"
                            href="/">
                            {{ $t('marketing_pages.already_signed_up') }}
                        </a>
                        <a class="focus:no-underline hover:bg-blue-600 hover:border-blue-600 hover:no-underline lg:mt-0 py-5px px-3 mt-2 font-medium text-white bg-blue-500 border border-blue-500 rounded"
                            id="sign-up-header-link"
                            href="/budgets/sign-up">
                            {{ $t('marketing_pages.sign_up') }}
                        </a>
                    </nav>
                </div>
            </div>
        </header>
        <section class="blue-masthead">
            <div class="container px-4 mx-auto text-center text-white" style="padding-top: calc(5rem + 60px); padding-bottom: calc(5rem + 150px);">
                <div class="max-w-3xl mx-auto my-2">
                    <h1 class="lg:text-2xl mt-0 mb-4 text-xl font-light">
                        {{ $t('marketing_pages.budgets_masthead_line_1') }}
                    </h1>
                    <p class="lg:text-lg text-base font-light leading-tight">
                        {{ $t('marketing_pages.budgets_masthead_line_2') }}
                    </p>
                    <div class="flex justify-center mt-10">
                        <a class="focus:no-underline hover:no-underline hover:shadow-lg lg:px-12 inline-block px-4 py-2 text-base font-medium text-blue-500 bg-blue-100 border border-transparent rounded cursor-pointer"
                            href="/budgets/sign-up"
                            id="sign-up-masthead-link">
                            {{ $t('marketing_pages.sign_up_cta') }}
                        </a>
                    </div>
                </div>
            </div>
        </section>
        <section class="bg-gray-200">
            <div class="container relative px-4 mx-auto" style="height: calc(499px - 154px);">
                <div v-if="!videoIsOpen" @click="playIntroVideo" class="md:mx-0 absolute inset-0 flex items-center justify-center w-auto mx-4 bg-cover rounded-lg shadow-2xl" id="play-intro-video-button" style="background-image: url('/images/marketing/budgets-main-screenshot.png'); height: 499px; margin-top: -154px;">
                    <div class="hover:scale-125 inline-flex items-center justify-center w-16 h-16 text-blue-100 transition duration-700 ease-in-out transform bg-blue-500 rounded-full cursor-pointer">
                        <svg class="w-8 h-8 ml-1" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>
                    </div>
                </div>
                <div v-else class="md:mx-0 inset-0 flex items-center justify-center w-auto mx-4 rounded-lg" style="height: 499px; margin-top: -154px;">
                    <intro-video :sources="video.sources" :options="video.options"></intro-video>
                </div>
            </div>
        </section>
        <section id="counters-section" class="pt-16 pb-20 bg-gray-200">
            <div class="container px-4 mx-auto">
                <div class="max-w-3xl mx-auto mb-12 text-center">
                    <h2 class="lg:text-2xl mb-1 text-xl font-medium leading-tight">
                        {{ $t('marketing_pages.budgets_counters_section_title') }}
                    </h2>
                    <p>
                        {{ $t('marketing_pages.budgets_counters_section_subtitle') }}
                    </p>
                </div>
                <div class="lg:grid-cols-2 lg:grid-rows-2 grid grid-cols-1 grid-rows-4 row-gap-6 col-gap-8">
                    <template v-for="(counter, index) in counters">
                        <counter :key="index" :text="counter.text"></counter>
                    </template>
                </div>
            </div>
        </section>
        <section id="features-section" class="pt-12 pb-20 bg-white">
            <div class="container px-4 mx-auto">
                <div class="max-w-3xl mx-auto mb-12 text-center">
                    <h2 class="lg:text-2xl mb-1 text-xl font-medium leading-tight">
                        {{ $t('marketing_pages.budgets_features_section_title') }}
                    </h2>
                    <p>
                        {{ $t('marketing_pages.budgets_features_section_subtitle') }}
                    </p>
                </div>
                <div class="lg:grid-cols-3 lg:grid-rows-2 grid grid-cols-1 grid-rows-6 row-gap-6 col-gap-8">
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path><path d="M13.73 21a2 2 0 0 1-3.46 0"></path></svg>
                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_1_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_1_info') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path></svg>                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_2_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_2_info') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_3_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_3_info') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_4_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_4_info') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"></polyline><path d="M3 11V9a4 4 0 0 1 4-4h14"></path><polyline points="7 23 3 19 7 15"></polyline><path d="M21 13v2a4 4 0 0 1-4 4H3"></path></svg>
                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_5_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_5_info') }}
                        </p>
                    </div>
                    <div class="flex flex-col items-center px-4">
                        <div class="inline-flex items-center justify-center w-12 h-12 text-blue-500 bg-blue-100 rounded-full">
                            <svg class="w-6 h-6" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                        </div>
                        <h3 class="text-xl font-medium text-center">
                            {{ $t('marketing_pages.budgets_feature_6_title') }}
                        </h3>
                        <p class="m-0 text-sm font-normal text-center">
                            {{ $t('marketing_pages.budgets_feature_6_info') }}
                        </p>
                    </div>
                </div>
            </div>
        </section>
        <section id="testimonials-section" class="bg-navy-light pt-12 pb-20">
            <div class="container px-4 mx-auto">
                <div class="max-w-3xl mx-auto mb-12 text-center">
                    <h2 class="lg:text-2xl mb-1 text-xl font-medium leading-tight text-gray-300">
                        {{ $t('marketing_pages.budgets_testimonials_section_title') }}
                    </h2>
                    <p class="text-gray-400">
                        {{ $t('marketing_pages.budgets_testimonials_section_subtitle') }}
                    </p>
                </div>
                <vue-glide ref="slider" :per-view="2" :gap="28" :bound="true" :bullet="true" :breakpoints="{ 1023: { perView: 1 }, }" @glide:run-before="setSliderSteps">
                    <vue-glide-slide v-for="(testimonial, index) in testimonials" :key="index">
                        <div class="flex flex-col h-full p-6 mx-12 bg-gray-200 rounded shadow-sm" :class="[ index % 2 === 0 ? 'lg:ml-0' : 'lg:mr-0' ]">
                            <svg class="block w-5 h-5 mb-4 text-center text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 975.036 975.036">
                                <path d="M925.036 57.197h-304c-27.6 0-50 22.4-50 50v304c0 27.601 22.4 50 50 50h145.5c-1.9 79.601-20.4 143.3-55.4 191.2-27.6 37.8-69.399 69.1-125.3 93.8-25.7 11.3-36.8 41.7-24.8 67.101l36 76c11.6 24.399 40.3 35.1 65.1 24.399 66.2-28.6 122.101-64.8 167.7-108.8 55.601-53.7 93.7-114.3 114.3-181.9 20.601-67.6 30.9-159.8 30.9-276.8v-239c0-27.599-22.401-50-50-50zM106.036 913.497c65.4-28.5 121-64.699 166.9-108.6 56.1-53.7 94.4-114.1 115-181.2 20.6-67.1 30.899-159.6 30.899-277.5v-239c0-27.6-22.399-50-50-50h-304c-27.6 0-50 22.4-50 50v304c0 27.601 22.4 50 50 50h145.5c-1.9 79.601-20.4 143.3-55.4 191.2-27.6 37.8-69.4 69.1-125.3 93.8-25.7 11.3-36.8 41.7-24.8 67.101l35.9 75.8c11.601 24.399 40.501 35.2 65.301 24.399z"></path>
                            </svg>
                            <p class="flex-1 pb-4 mb-4 text-sm leading-relaxed text-gray-600 border-b border-gray-400" v-html="testimonial.text"></p>
                            <div class="inline-flex items-center">
                                <img class="flex-shrink-0 object-cover object-center w-12 h-12 border border-gray-300 rounded-full" :src="testimonial.picture" :alt="testimonial.author">
                                <span class="flex flex-col flex-grow pl-4">
                                    <span class="title-font text-sm font-medium text-gray-700">{{ testimonial.author }}</span>
                                    <span class="text-sm text-gray-600" v-html="testimonial.job_title"></span>
                                </span>
                            </div>
                        </div>
                    </vue-glide-slide>
                    <template slot="control">
                        <button data-glide-dir="<">
                            <div class="inline-flex items-center justify-center w-6 h-6 mt-px ml-1 text-gray-700 bg-white rounded-full opacity-50">
                                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>
                            </div>
                        </button>
                        <button data-glide-dir=">">
                            <div class="inline-flex items-center justify-center w-6 h-6 mt-px mr-1 text-gray-700 bg-white rounded-full opacity-50">
                                <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"></line><polyline points="12 5 19 12 12 19"></polyline></svg>
                            </div>
                        </button>
                    </template>
                </vue-glide>
            </div>
        </section>
        <section id="prices-section" class="pt-12 pb-20 bg-white">
            <div class="container px-4 mx-auto">
                <div class="max-w-3xl mx-auto mb-12 text-center">
                    <h2 class="lg:text-2xl mb-1 text-xl font-medium leading-tight">
                        {{ $t('marketing_pages.budgets_prices_section_title') }}
                    </h2>
                    <p>
                        {{ $t('marketing_pages.budgets_prices_section_subtitle') }}
                    </p>
                </div>
                <div class="flex flex-wrap -m-4">
                    <div v-for="subscriptionPlan in subscriptionPlans" class="md:w-1/3 w-full p-4">
                        <div class="border-gray-card relative flex flex-col h-full overflow-hidden bg-gray-100 border rounded-md">
                            <div class="flex flex-col items-center pt-6 bg-white border-b border-gray-200">
                                <h1 class="flex items-center mt-0 mb-4 text-2xl leading-none">
                                    <span class="text-gray-700">&euro;{{ subscriptionPlan.min_price }} - &euro;{{ subscriptionPlan.max_price }}</span>
                                    <span class="text-2xs ml-1 font-normal">/ {{ $t('organization_payments.month') }}</span>
                                </h1>
                                <h2 class="mt-0 mb-3 text-xs font-medium tracking-widest text-gray-400 uppercase">
                                    {{ subscriptionPlan.name }}
                                </h2>
                            </div>
                            <div class="px-4">
                                <div class="flex flex-col items-center py-4 text-sm font-medium">
                                    <template v-if="subscriptionPlan.min_user_limit && subscriptionPlan.max_user_limit">
                                        <div class="my-px text-center">{{ `${subscriptionPlan.min_user_limit} - ${subscriptionPlan.max_user_limit} ${$t('organization_payments.users')}` }}</div>
                                    </template>
                                    <template v-else-if="!subscriptionPlan.min_user_limit && !subscriptionPlan.max_user_limit">
                                        <div class="my-px text-center">{{ `${$t('organization_payments.unlimited_male')} ${$t('organization_payments.users')}` }}</div>
                                    </template>
                                    <template v-if="subscriptionPlan.min_main_entity_limit && subscriptionPlan.max_main_entity_limit">
                                        <div class="my-px text-center">{{ `${subscriptionPlan.min_main_entity_limit} - ${subscriptionPlan.max_main_entity_limit} ${$t('organization_payments.subdivisions')}` }}</div>
                                    </template>
                                    <template v-else-if="subscriptionPlan.min_main_entity_limit && !subscriptionPlan.max_main_entity_limit">
                                        <div class="my-px text-center">{{ `${subscriptionPlan.min_main_entity_limit} - ${$t('organization_payments.unlimited_female')} ${$t('organization_payments.subdivisions')}` }}</div>
                                    </template>
                                    <div class="my-px text-center">{{ $t('organization_applications.unlimited_data_sources') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="flex justify-center mt-10">
                    <a class="focus:no-underline hover:bg-blue-600 hover:no-underline lg:px-12 px-4 py-2 text-base font-medium text-white bg-blue-500 rounded"
                        id="sign-up-prices-section-link"
                        href="/budgets/sign-up">
                        {{ $t('marketing_pages.sign_up_cta') }}
                    </a>
                </div>
            </div>
        </section>
        <section id="faq-section" class="pt-12 pb-20 bg-gray-200">
            <div class="container px-4 mx-auto">
                <div class="max-w-3xl mx-auto mb-12 text-center">
                    <h2 class="lg:text-2xl mb-1 text-xl font-medium leading-tight">
                        {{ $t('marketing_pages.faq_section_title') }}
                    </h2>
                    <p>
                        {{ $t('marketing_pages.faq_section_subtitle') }}
                    </p>
                </div>
                <div class="flex flex-wrap mb-8 -m-4">
                    <template v-for="(section, index) in faqSections">
                        <faq-section :key="index" :index="index" :section="section">
                            <svg v-if="index === 0" class="w-8 h-8" :class="[ section.open ? 'text-blue-600' : 'text-gray-500' ]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="2" y1="12" x2="22" y2="12"></line><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path></svg>
                            <svg v-else-if="index === 1" class="w-8 h-8 text-gray-500" :class="[ section.open ? 'text-blue-600' : 'text-gray-500' ]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path><polyline points="22 4 12 14.01 9 11.01"></polyline></svg>
                            <svg v-else-if="index === 2" class="w-8 h-8 text-gray-500" :class="[ section.open ? 'text-blue-600' : 'text-gray-500' ]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon></svg>
                            <svg v-else-if="index === 3" class="w-8 h-8 text-gray-500" :class="[ section.open ? 'text-blue-600' : 'text-gray-500' ]" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path></svg>
                        </faq-section>
                    </template>
                </div>
                <div class="max-w-4xl mx-auto">
                    <template v-for="(item, index) in filteredFaqItems">
                        <faq-item :key="index" :index="index" :item="item"></faq-item>
                    </template>
                </div>
            </div>
        </section>
        <footer class="bg-white border-t">
            <div class="container px-4 py-2 mx-auto text-center">
                {{ new Date().getFullYear() }} &copy; {{ $t('globals.budgets') }}
                •
                <a class="text-blue-500" href="/terms-and-conditions">{{ $t('globals.terms_and_conditions') }}</a>
                •
                <a class="text-blue-500" href="/privacy-policy">{{ $t('globals.privacy') }}</a>
            </div>
        </footer>
    </div>
</template>

<script>
import Counter from './partials/counter.vue';
import FaqItem from './partials/faq-item.vue';
import FaqSection from './partials/faq-section.vue';
import IntroVideo from './partials/intro-video.vue';
import LogoInverse from '../partials/logo-inverse.vue';
import { Glide, GlideSlide } from 'vue-glide-js';

export default {
    components: {
        'counter': Counter,
        'faq-item': FaqItem,
        'faq-section': FaqSection,
        'intro-video': IntroVideo,
        'logo-inverse': LogoInverse,
        [Glide.name]: Glide,
        [GlideSlide.name]: GlideSlide,
    },

    props: {
        counters: {
            type: Array,
            required: true,
        },
        testimonials: {
            type: Array,
            required: true,
        },
        subscriptionPlans: {
            type: Array,
            required: true,
        },
        initialFaqSections: {
            type: Array,
            required: true,
        },
        initialFaqs: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            atTop: true,
            menuIsOpen: false,
            videoIsOpen: false,
            video: this.getVideo(),
            faqSections: this.initialFaqSections,
            faqs: this.initialFaqs,
        };
    },

    created() {
        window.addEventListener('scroll', this.handleScroll);

        Bus.$on('openFaqSection', ({ index }) => {
            this.faqSections = this.faqSections.map((section, sectionIndex) => {
                var newSection = Object.assign({}, section);
                newSection.open = sectionIndex === index;
                return newSection;
            });
        });

        Bus.$on('toggleFaqItem', ({ index }) => {
            this.filteredFaqItems.forEach((faqItem, faqItemIndex) => {
                if (faqItemIndex === index) {
                    this.filteredFaqItems[faqItemIndex].open = !this.filteredFaqItems[faqItemIndex].open;
                } else {
                    this.filteredFaqItems[faqItemIndex].open = false;
                }
            });
        });
    },

    destroyed() {
        window.removeEventListener('scroll', this.handleScroll);
    },

    computed: {
        filteredFaqItems() {
            const currentFaqSection = this.faqSections.find(section => section.open === true);
            let filteredFaqItems = this.faqs.filter(faq => faq.section === currentFaqSection.slug);
            return filteredFaqItems;
        },
    },

    methods: {
        getVideo() {
            let videoPath = '/videos/reefkig-budgets-promo.mp4';
            if (window.Globals.locale === 'en') {
                videoPath = '/videos/reefkig-budgets-promo-en.mp4';
            }

            return {
                sources: [{ src: videoPath, type: 'video/mp4'}],
                options: {
                    autoplay: true,
                    volume: 0.6,
                    poster: '/images/marketing/budgets-main-screenshot.png'
                }
            };
        },

        handleScroll(event) {
            this.atTop = (window.pageYOffset < 100) ? true : false;
        },

        playIntroVideo() {
            this.videoIsOpen = true;
        },

        setSliderSteps(event) {
            if (event.direction === '<' || event.direction === '>') {
                const scrollSteps = this.$refs.slider.glide.settings.perView;
                event.steps = event.direction === '>' ? -scrollSteps : scrollSteps;
            }
        },
    },
};
</script>

<style>
    /*! purgecss start ignore */
    .glide {
        position: relative;
        width: 100%;
        -webkit-box-sizing: border-box;
        box-sizing: border-box;
    }
    .glide * {
        -webkit-box-sizing: inherit;
        box-sizing: inherit;
    }
    .glide__slides,
    .glide__track {
        overflow: hidden;
    }
    .glide__slides {
        @apply flex items-stretch;
        position: relative;
        width: 100%;
        list-style: none;
        -webkit-backface-visibility: hidden;
        backface-visibility: hidden;
        -webkit-transform-style: preserve-3d;
        transform-style: preserve-3d;
        -ms-touch-action: pan-Y;
        touch-action: pan-Y;
        padding: 0;
        white-space: nowrap;
        will-change: transform;
    }
    .glide__slide,
    .glide__slides--dragging {
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }
    .glide__slide {
        white-space: normal;
        -webkit-touch-callout: none;
        -webkit-tap-highlight-color: transparent;
    }
    .glide__slide a {
        -webkit-user-select: none;
        user-select: none;
        -webkit-user-drag: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }
    .glide__arrows,
    .glide__bullets {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
    }
    .glide--rtl {
        direction: rtl;
    }
    div[data-glide-el=controls] {
        @apply absolute inset-x-0;
        top: calc(50% - 1.5rem);
    }
    button[data-glide-dir="<"], button[data-glide-dir=">"] {
        @apply absolute border-0 outline-none cursor-pointer;
    }
    button[data-glide-dir="<"] {
        @apply left-0;
    }
    button[data-glide-dir=">"] {
        @apply right-0;
    }
    .glide__bullets {
        @apply mt-6 text-center;
    }
    @screen lg {
        .glide__bullet:nth-child(even) {
            @apply hidden;
        }
    }
    .glide__bullet {
        @apply bg-gray-200 border-0 cursor-pointer h-4 outline-none rounded-full w-4;
    }
    .glide__bullet:focus {
        @apply outline-none;
    }
    .glide__bullet+.glide__bullet {
        @apply ml-3;
    }
    .glide__bullet--active,
    .glide__bullet--active:hover {
        @apply bg-blue-600;
    }
    /*! purgecss end ignore */
</style>
